import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useState } from "react";

const AccessibilityForm = ({ embedded = false }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const accessibilityFormUrl =
    process.env.REACT_APP_ACCESSIBILITY_FORM_URL || "https://stage.survey.firmaciudadana.pjm.gob.ar/save";
  const accessibilityFormApiKey =
    process.env.REACT_APP_ACCESSIBILITY_FORM_API_KEY || "AAAA-BBBB-123498760";
  const stackedOptionsSx = {
    display: "grid",
    gap: "6px",
    gridTemplateColumns: "1fr",
  };
  const requiredFieldNames = [
    "uso-firma",
    "rol-plataforma",
    "frecuencia-uso",
    "dispositivo-principal",
    "propiedad-dispositivo",
    "tipo-conexion",
    "diversidad-funcional",
    "experiencia-general",
    "firma-sin-ayuda",
    "barreras",
    "claridad-proceso",
    "mensajes-comprensibles",
    "facilita-acceso",
    "contribuyen-inclusion",
    "recomendarias",
    "rango-edad",
  ];

  const findMissingRequiredFields = (formData) =>
    requiredFieldNames.filter((fieldName) => !formData.get(fieldName));

  const handleSubmit = async (event) => {
    event.preventDefault();

    const form = event.currentTarget;
    const formData = new FormData(form);
    const getSingleValue = (fieldName) => formData.get(fieldName) || null;
    const getMultipleValues = (fieldName) => formData.getAll(fieldName);
    const missingRequiredFields = findMissingRequiredFields(formData);

    if (missingRequiredFields.length > 0) {
      setSubmitMessage("Revisá si el formulario está completo antes de enviarlo.");
      const firstMissingField = form.querySelector(`[name="${missingRequiredFields[0]}"]`);
      firstMissingField?.focus();
      return;
    }

    const payload = {
      submittedAt: new Date().toISOString(),
      responses: {
        informacionGeneral: {
          usoFirma: getSingleValue("uso-firma"),
          rolPlataforma: getSingleValue("rol-plataforma"),
          frecuenciaUso: getSingleValue("frecuencia-uso"),
        },
        dispositivoYContexto: {
          dispositivoPrincipal: getSingleValue("dispositivo-principal"),
          propiedadDispositivo: getSingleValue("propiedad-dispositivo"),
          tipoConexion: getSingleValue("tipo-conexion"),
        },
        accesibilidadYDiversidades: {
          diversidadFuncional: getSingleValue("diversidad-funcional"),
          tiposDiversidadFuncional: getMultipleValues("tipos-diversidad-funcional"),
          tecnologiasAsistencia: getMultipleValues("tecnologias-asistencia"),
        },
        experienciaUso: {
          experienciaGeneral: getSingleValue("experiencia-general"),
          firmaSinAyuda: getSingleValue("firma-sin-ayuda"),
          barreras: getSingleValue("barreras"),
          barrerasDetalle: getSingleValue("barreras-detalle"),
        },
        firmaYComprension: {
          claridadProceso: getSingleValue("claridad-proceso"),
          mensajesComprensibles: getSingleValue("mensajes-comprensibles"),
        },
        impactoYPercepcion: {
          facilitaAcceso: getSingleValue("facilita-acceso"),
          contribuyenInclusion: getSingleValue("contribuyen-inclusion"),
          recomendarias: getSingleValue("recomendarias"),
        },
        edadYMomentoVida: {
          rangoEdad: getSingleValue("rango-edad"),
        },
      },
    };

    setIsSubmitting(true);
    setSubmitMessage("");
    console.log("Accessibility form payload:", payload);

    try {
      await axios.post(accessibilityFormUrl, payload, {
        headers: {
          "Content-Type": "application/json",
          "X-API-KEY": accessibilityFormApiKey,
        },
      });

      setSubmitMessage("Muchas gracias. Tu respuesta fue enviada correctamente.");
      form.reset();
    } catch (error) {
      const statusCode = error.response?.status;
      setSubmitMessage(
        statusCode === 422
          ? "Revisá si el formulario está completo antes de enviarlo."
          : "No pudimos enviar el formulario en este momento. Intentá nuevamente en unos minutos."
      );
      console.error("Error sending accessibility form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        padding: embedded ? 0 : "24px 16px 48px",
        background: embedded
          ? "transparent"
          : "linear-gradient(180deg, rgba(49, 96, 148, 0.08) 0%, rgba(255, 255, 255, 1) 55%)",
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          maxWidth: embedded ? "100%" : 1080,
          width: "100%",
          backgroundColor: "white",
          borderRadius: embedded ? 0 : "12px",
          padding: embedded ? 0 : { xs: "16px", md: "28px" },
          boxShadow: embedded ? "none" : "0 6px 18px rgba(0, 0, 0, 0.08)",
          display: "grid",
          gap: { xs: "16px", md: "22px" },
          "& .MuiFormLabel-root": {
            fontFamily: "robotoMedium",
            color: "#233548",
            fontSize: "1rem",
            lineHeight: 1.5,
          },
          "& .MuiFormControlLabel-label": {
            color: "#243140",
            lineHeight: 1.45,
          },
          "& .MuiRadio-root.Mui-checked, & .MuiCheckbox-root.Mui-checked": {
            color: "#316094",
          },
        }}
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, rgba(49, 96, 148, 0.12) 0%, rgba(255, 255, 255, 1) 65%)",
            borderRadius: embedded ? 0 : "12px",
            padding: { xs: "18px", md: "22px" },
            border: "1px solid rgba(49, 96, 148, 0.18)",
            display: "grid",
            gap: "10px",
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              backgroundColor: "rgba(49, 96, 148, 0.14)",
              color: "#1f3f66",
              padding: "4px 10px",
              borderRadius: "999px",
              width: "fit-content",
              fontFamily: "robotoMedium",
              fontSize: "0.75rem",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            Experiencia y Diversidad
          </Box>
          {!embedded && (
            <Typography
              variant="h5"
              sx={{ fontFamily: "robotoMedium", fontWeight: "bold", color: "#1f3f66" }}
            >
              Formulario de Experiencia y Diversidad
            </Typography>
          )}
          <Typography
            variant="subtitle1"
            sx={{ fontFamily: "robotoMedium", color: "#1f3f66", fontSize: { xs: "1rem", md: "1.05rem" } }}
          >
            Firma Ciudadana – Evaluación de Accesibilidad e Inclusión
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "robotoMedium",
              color: "#3f4a57",
              maxWidth: 900,
              lineHeight: 1.55,
            }}
          >
            Este formulario tiene como objetivo conocer la diversidad de personas que utilizan la plataforma Firma
            Ciudadana y su experiencia de uso. La información recolectada es confidencial, anónima y se utiliza
            exclusivamente para mejorar los procesos, la accesibilidad y la inclusión del sistema.
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "robotoMedium",
              color: "#5c6a79",
              lineHeight: 1.55,
            }}
          >
            Puede responder con teclado, lector de pantalla o puntero. Los campos con varias opciones permiten marcar
            más de una respuesta cuando se indica.
          </Typography>
        </Box>

        <Box
          sx={{
            border: "1px solid rgba(0, 0, 0, 0.08)",
            borderRadius: "12px",
            padding: { xs: "16px", md: "20px" },
            backgroundColor: "#fafafa",
            display: "grid",
            gap: "16px",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: "robotoMedium",
              fontWeight: "bold",
              marginBottom: "12px",
              color: "#1f3f66",
            }}
          >
            1. Información general de uso
          </Typography>
          <Box sx={{ display: "grid", gap: "16px", gridTemplateColumns: "1fr" }}>
            <FormControl component="fieldset" sx={{ display: "grid", gap: "8px" }}>
              <FormLabel id="uso-firma-label" component="legend">
                1.1 ¿Ha utilizado Firma Ciudadana para firmar un documento?
              </FormLabel>
              <RadioGroup aria-labelledby="uso-firma-label" name="uso-firma" sx={stackedOptionsSx}>
                <FormControlLabel value="si" control={<Radio />} label="Sí" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
            <FormControl component="fieldset" sx={{ display: "grid", gap: "8px" }}>
              <FormLabel id="rol-plataforma-label" component="legend">
                1.2 ¿Desde qué rol utilizaste la plataforma?
              </FormLabel>
              <RadioGroup aria-labelledby="rol-plataforma-label" name="rol-plataforma" sx={stackedOptionsSx}>
                <FormControlLabel value="firmante" control={<Radio />} label="Firmante" />
                <FormControlLabel value="publicador" control={<Radio />} label="Publicador" />
                <FormControlLabel value="ambos" control={<Radio />} label="Ambos" />
                <FormControlLabel value="no-seguro" control={<Radio />} label="No estoy seguro/a" />
              </RadioGroup>
            </FormControl>
          </Box>
          <FormControl component="fieldset" sx={{ display: "grid", gap: "8px" }}>
            <FormLabel id="frecuencia-uso-label" component="legend">
              1.3 ¿Cuántas veces utilizaste la plataforma aproximadamente?
            </FormLabel>
            <RadioGroup aria-labelledby="frecuencia-uso-label" name="frecuencia-uso" sx={stackedOptionsSx}>
              <FormControlLabel value="una-vez" control={<Radio />} label="Una vez" />
              <FormControlLabel value="2-5" control={<Radio />} label="Entre 2 y 5 veces" />
              <FormControlLabel value="mas-5" control={<Radio />} label="Más de 5 veces" />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box
          sx={{
            border: "1px solid rgba(0, 0, 0, 0.08)",
            borderRadius: "12px",
            padding: { xs: "16px", md: "20px" },
            backgroundColor: "white",
            display: "grid",
            gap: "16px",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: "robotoMedium",
              fontWeight: "bold",
              marginBottom: "12px",
              color: "#1f3f66",
            }}
          >
            2. Dispositivo y contexto de uso
          </Typography>
          <FormControl component="fieldset" sx={{ display: "grid", gap: "8px" }}>
            <FormLabel id="dispositivo-principal-label" component="legend">
              2.1 ¿Desde qué dispositivo accediste principalmente?
            </FormLabel>
            <RadioGroup aria-labelledby="dispositivo-principal-label" name="dispositivo-principal" sx={stackedOptionsSx}>
              <FormControlLabel value="celular" control={<Radio />} label="Teléfono celular" />
              <FormControlLabel value="computadora" control={<Radio />} label="Computadora" />
              <FormControlLabel value="tableta" control={<Radio />} label="Tableta" />
              <FormControlLabel value="varios" control={<Radio />} label="Más de un dispositivo" />
            </RadioGroup>
          </FormControl>
          <FormControl component="fieldset" sx={{ display: "grid", gap: "8px" }}>
            <FormLabel id="propiedad-dispositivo-label" component="legend">
              2.2 El dispositivo utilizado era:
            </FormLabel>
            <RadioGroup aria-labelledby="propiedad-dispositivo-label" name="propiedad-dispositivo" sx={stackedOptionsSx}>
              <FormControlLabel value="propio" control={<Radio />} label="Propio" />
              <FormControlLabel value="compartido" control={<Radio />} label="Compartido" />
              <FormControlLabel value="prestado" control={<Radio />} label="Prestado" />
            </RadioGroup>
          </FormControl>
          <FormControl component="fieldset" sx={{ display: "grid", gap: "8px" }}>
            <FormLabel id="tipo-conexion-label" component="legend">
              2.3 ¿Qué tipo de conexión utilizas habitualmente?
            </FormLabel>
            <RadioGroup aria-labelledby="tipo-conexion-label" name="tipo-conexion" sx={stackedOptionsSx}>
              <FormControlLabel value="datos-moviles" control={<Radio />} label="Datos móviles" />
              <FormControlLabel value="wifi-domiciliario" control={<Radio />} label="Wi-Fi domiciliario" />
              <FormControlLabel value="wifi-publico" control={<Radio />} label="Wi-Fi público" />
              <FormControlLabel value="limitada" control={<Radio />} label="Conectividad limitada o inestable" />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box
          sx={{
            border: "1px solid rgba(0, 0, 0, 0.08)",
            borderRadius: "12px",
            padding: { xs: "16px", md: "20px" },
            backgroundColor: "#fafafa",
            display: "grid",
            gap: "16px",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: "robotoMedium",
              fontWeight: "bold",
              marginBottom: "12px",
              color: "#1f3f66",
            }}
          >
            3. Accesibilidad y diversidades
          </Typography>
          <FormControl component="fieldset" sx={{ display: "grid", gap: "8px" }}>
            <FormLabel id="diversidad-funcional-label" component="legend">
              3.1 ¿Te identificás como una persona con discapacidad o con alguna diversidad funcional?
            </FormLabel>
            <RadioGroup aria-labelledby="diversidad-funcional-label" name="diversidad-funcional" sx={stackedOptionsSx}>
              <FormControlLabel value="si" control={<Radio />} label="Sí" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
              <FormControlLabel value="prefiero-no-responder" control={<Radio />} label="Prefiero no responder" />
            </RadioGroup>
          </FormControl>
          <FormControl component="fieldset" sx={{ display: "grid", gap: "8px" }}>
            <FormLabel id="tipos-diversidad-funcional-label" component="legend">
              3.2 En caso afirmativo, ¿con cuál o cuáles te identificás? (Puede marcar más de una opción)
            </FormLabel>
            <FormGroup aria-labelledby="tipos-diversidad-funcional-label" sx={stackedOptionsSx}>
              <FormControlLabel
                control={<Checkbox name="tipos-diversidad-funcional" value="visual" />}
                label="Visual"
              />
              <FormControlLabel
                control={<Checkbox name="tipos-diversidad-funcional" value="auditiva" />}
                label="Auditiva"
              />
              <FormControlLabel
                control={<Checkbox name="tipos-diversidad-funcional" value="motriz" />}
                label="Motriz"
              />
              <FormControlLabel
                control={<Checkbox name="tipos-diversidad-funcional" value="cognitiva-intelectual" />}
                label="Cognitiva / intelectual"
              />
              <FormControlLabel
                control={<Checkbox name="tipos-diversidad-funcional" value="psicosocial" />}
                label="Psicosocial"
              />
              <FormControlLabel control={<Checkbox name="tipos-diversidad-funcional" value="otra" />} label="Otra" />
              <FormControlLabel
                control={<Checkbox name="tipos-diversidad-funcional" value="prefiero-no-especificar" />}
                label="Prefiero no especificar"
              />
            </FormGroup>
          </FormControl>
          <FormControl component="fieldset" sx={{ display: "grid", gap: "8px" }}>
            <FormLabel id="tecnologias-asistencia-label" component="legend">
              3.3 ¿Utilizas habitualmente apoyos o tecnologías de asistencia?
            </FormLabel>
            <FormGroup aria-labelledby="tecnologias-asistencia-label" sx={stackedOptionsSx}>
              <FormControlLabel
                control={<Checkbox name="tecnologias-asistencia" value="lector-pantalla" />}
                label="Lector de pantalla"
              />
              <FormControlLabel
                control={<Checkbox name="tecnologias-asistencia" value="ampliador-texto" />}
                label="Ampliador de texto"
              />
              <FormControlLabel
                control={<Checkbox name="tecnologias-asistencia" value="teclado-mouse-adaptado" />}
                label="Teclado o mouse adaptado"
              />
              <FormControlLabel
                control={<Checkbox name="tecnologias-asistencia" value="asistencia-otra-persona" />}
                label="Asistencia de otra persona"
              />
              <FormControlLabel
                control={<Checkbox name="tecnologias-asistencia" value="no-utilizo-apoyos" />}
                label="No utilizo apoyos"
              />
              <FormControlLabel control={<Checkbox name="tecnologias-asistencia" value="otro" />} label="Otro" />
            </FormGroup>
          </FormControl>
        </Box>

        <Box
          sx={{
            border: "1px solid rgba(0, 0, 0, 0.08)",
            borderRadius: "12px",
            padding: { xs: "16px", md: "20px" },
            backgroundColor: "white",
            display: "grid",
            gap: "16px",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: "robotoMedium",
              fontWeight: "bold",
              marginBottom: "12px",
              color: "#1f3f66",
            }}
          >
            4. Experiencia de uso de la plataforma
          </Typography>
          <FormControl component="fieldset" sx={{ display: "grid", gap: "8px" }}>
            <FormLabel id="experiencia-general-label" component="legend">
              4.1 En general, ¿cómo calificarías tu experiencia con Firma Ciudadana?
            </FormLabel>
            <RadioGroup aria-labelledby="experiencia-general-label" name="experiencia-general" sx={stackedOptionsSx}>
              <FormControlLabel value="muy-buena" control={<Radio />} label="Muy buena" />
              <FormControlLabel value="buena" control={<Radio />} label="Buena" />
              <FormControlLabel value="regular" control={<Radio />} label="Regular" />
              <FormControlLabel value="mala" control={<Radio />} label="Mala" />
              <FormControlLabel value="muy-mala" control={<Radio />} label="Muy mala" />
            </RadioGroup>
          </FormControl>
          <FormControl component="fieldset" sx={{ display: "grid", gap: "8px" }}>
            <FormLabel id="firma-sin-ayuda-label" component="legend">
              4.2 ¿Pudiste completar la firma sin ayuda externa?
            </FormLabel>
            <RadioGroup aria-labelledby="firma-sin-ayuda-label" name="firma-sin-ayuda" sx={stackedOptionsSx}>
              <FormControlLabel value="si" control={<Radio />} label="Sí" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
              <FormControlLabel value="algunos-casos" control={<Radio />} label="En algunos casos" />
            </RadioGroup>
          </FormControl>
          <FormControl component="fieldset" sx={{ display: "grid", gap: "8px" }}>
            <FormLabel id="barreras-label" component="legend">
              4.3 ¿Encontraste barreras o dificultades durante el proceso?
            </FormLabel>
            <RadioGroup aria-labelledby="barreras-label" name="barreras" sx={stackedOptionsSx}>
              <FormControlLabel value="no" control={<Radio />} label="No" />
              <FormControlLabel value="si" control={<Radio />} label="Sí" />
            </RadioGroup>
          </FormControl>
          <TextField
            id="barreras-detalle"
            label="Si respondió sí, por favor indique cuáles"
            name="barreras-detalle"
            variant="outlined"
            multiline
            minRows={3}
            helperText="Campo opcional para describir las barreras encontradas."
            fullWidth
            sx={{
              backgroundColor: "white",
              "& .MuiOutlinedInput-root": {
                alignItems: "flex-start",
              },
            }}
          />
        </Box>

        <Box
          sx={{
            border: "1px solid rgba(0, 0, 0, 0.08)",
            borderRadius: "12px",
            padding: { xs: "16px", md: "20px" },
            backgroundColor: "#fafafa",
            display: "grid",
            gap: "16px",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: "robotoMedium",
              fontWeight: "bold",
              marginBottom: "12px",
              color: "#1f3f66",
            }}
          >
            5. Firma y comprensión del proceso
          </Typography>
          <FormControl component="fieldset" sx={{ display: "grid", gap: "8px" }}>
            <FormLabel id="claridad-proceso-label" component="legend">
              5.1 El proceso de firma te resultó:
            </FormLabel>
            <RadioGroup aria-labelledby="claridad-proceso-label" name="claridad-proceso" sx={stackedOptionsSx}>
              <FormControlLabel value="claro" control={<Radio />} label="Claro" />
              <FormControlLabel value="medianamente-claro" control={<Radio />} label="Medianamente claro" />
              <FormControlLabel value="poco-claro" control={<Radio />} label="Poco claro" />
              <FormControlLabel value="confuso" control={<Radio />} label="Confuso" />
            </RadioGroup>
          </FormControl>
          <FormControl component="fieldset" sx={{ display: "grid", gap: "8px" }}>
            <FormLabel id="mensajes-comprensibles-label" component="legend">
              5.2 ¿La información y los mensajes del sistema fueron comprensibles?
            </FormLabel>
            <RadioGroup aria-labelledby="mensajes-comprensibles-label" name="mensajes-comprensibles" sx={stackedOptionsSx}>
              <FormControlLabel value="si" control={<Radio />} label="Sí" />
              <FormControlLabel value="en-parte" control={<Radio />} label="En parte" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box
          sx={{
            border: "1px solid rgba(0, 0, 0, 0.08)",
            borderRadius: "12px",
            padding: { xs: "16px", md: "20px" },
            backgroundColor: "white",
            display: "grid",
            gap: "16px",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: "robotoMedium",
              fontWeight: "bold",
              marginBottom: "12px",
              color: "#1f3f66",
            }}
          >
            6. Impacto y percepción institucional
          </Typography>
          <FormControl component="fieldset" sx={{ display: "grid", gap: "8px" }}>
            <FormLabel id="facilita-acceso-label" component="legend">
              6.1 ¿Considerás que la plataforma facilita el acceso a trámites judiciales?
            </FormLabel>
            <RadioGroup aria-labelledby="facilita-acceso-label" name="facilita-acceso" sx={stackedOptionsSx}>
              <FormControlLabel value="si" control={<Radio />} label="Sí" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
              <FormControlLabel value="no-seguro" control={<Radio />} label="No estoy seguro/a" />
            </RadioGroup>
          </FormControl>
          <FormControl component="fieldset" sx={{ display: "grid", gap: "8px" }}>
            <FormLabel id="contribuyen-inclusion-label" component="legend">
              6.2 ¿Creés que soluciones como esta contribuyen a procesos más inclusivos?
            </FormLabel>
            <RadioGroup aria-labelledby="contribuyen-inclusion-label" name="contribuyen-inclusion" sx={stackedOptionsSx}>
              <FormControlLabel value="si" control={<Radio />} label="Sí" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
              <FormControlLabel value="en-parte" control={<Radio />} label="En parte" />
            </RadioGroup>
          </FormControl>
          <FormControl component="fieldset" sx={{ display: "grid", gap: "8px" }}>
            <FormLabel id="recomendarias-label" component="legend">
              6.3 ¿Recomendarías el uso de Firma Ciudadana a otras personas?
            </FormLabel>
            <RadioGroup aria-labelledby="recomendarias-label" name="recomendarias" sx={stackedOptionsSx}>
              <FormControlLabel value="si" control={<Radio />} label="Sí" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
              <FormControlLabel value="depende" control={<Radio />} label="Depende del caso" />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box
          sx={{
            border: "1px solid rgba(0, 0, 0, 0.08)",
            borderRadius: "12px",
            padding: { xs: "16px", md: "20px" },
            backgroundColor: "#fafafa",
            display: "grid",
            gap: "16px",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: "robotoMedium",
              fontWeight: "bold",
              marginBottom: "12px",
              color: "#1f3f66",
            }}
          >
            7. Edad y momento de vida
          </Typography>
          <FormControl component="fieldset" sx={{ display: "grid", gap: "8px" }}>
            <FormLabel id="rango-edad-label" component="legend">
              7.1 ¿En qué rango de edad estás?
            </FormLabel>
            <RadioGroup aria-labelledby="rango-edad-label" name="rango-edad" sx={stackedOptionsSx}>
              <FormControlLabel value="menos-18" control={<Radio />} label="Menos de 18 años" />
              <FormControlLabel value="18-29" control={<Radio />} label="18 a 29 años" />
              <FormControlLabel value="30-44" control={<Radio />} label="30 a 44 años" />
              <FormControlLabel value="45-59" control={<Radio />} label="45 a 59 años" />
              <FormControlLabel value="60-74" control={<Radio />} label="60 a 74 años" />
              <FormControlLabel value="75+" control={<Radio />} label="75 años o más" />
              <FormControlLabel value="prefiero-no-responder" control={<Radio />} label="Prefiero no responder" />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box
          sx={{
            position: embedded ? "sticky" : "static",
            bottom: 0,
            zIndex: 1,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "center" },
            gap: 1.5,
            padding: embedded ? "16px 0 0" : "0 0 4px",
            backgroundColor: embedded ? "rgba(255, 255, 255, 0.98)" : "transparent",
            borderTop: embedded ? "1px solid rgba(49, 96, 148, 0.12)" : "none",
          }}
        >
          <Typography
            variant="body2"
            aria-live="polite"
            sx={{
              fontFamily: "robotoMedium",
              color: submitMessage ? (submitMessage.includes("correctamente") ? "#2e7d32" : "#d32f2f") : "#506173",
              minHeight: "24px",
              textAlign: { xs: "left", md: "right" },
            }}
          >
            {submitMessage || "La información es anónima y se envía de forma segura."}
          </Typography>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              alignSelf: { xs: "stretch", md: "auto" },
              backgroundColor: "#316094",
              fontFamily: "robotoMedium",
              paddingX: "28px",
              paddingY: "10px",
              borderRadius: "999px",
              textTransform: "none",
              boxShadow: "0 10px 18px rgba(49, 96, 148, 0.2)",
              "&:hover": {
                backgroundColor: "#2a527f",
              },
            }}
          >
            {isSubmitting ? "Enviando..." : "Enviar formulario"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AccessibilityForm;
